// Bailey's ideas for this week: what's actually happening at the salon
// (gaps in the diary, a new review, someone not introduced yet, a moment
// coming up), each one ready to open as a post. Pure, so it's easy to test;
// SocialPage loads the inputs.
import { upcomingMoments } from './seasonal'
import { hasSeasonal, seasonalFor, seasonalLayout, withSalon } from '../kits'
import { usedSources } from './store'

const DAY = 86400000

// A date as a post the salon can share as it is: a tip when the date comes
// with one, otherwise an announcement in the season's design when there is
// one. Never a design's example times, offer price or voucher amount: those
// kinds need the salon's own facts, which an idea doesn't have.
const momentIdea = (m, salonName) => {
  const c = m.content || {}
  if (m.type === 'tips') return { type: 'tips', content: c }
  const cta = c.cta || (m.kind === 'gift' ? 'Ask about gift vouchers' : 'Book online, link in bio')
  const layout = hasSeasonal(m.id) ? seasonalFor(m.id, 'open') : null
  if (layout) return { type: 'announce', design: { family: 'layouts', layout }, content: { ...withSalon(seasonalLayout(layout).content(), salonName), cta } }
  return { type: 'announce', content: { eyebrow: c.eyebrow || m.name, headline: c.headline || m.name, body: '', cta } }
}
const firstName = (s) => String(s || '').trim().split(/\s+/)[0] || ''

// A review worth sharing: kind, not too long, not shared before
const shareable = (r, used) => r && r.text && (r.rating || 5) >= 4
  && r.text.length >= 30 && r.text.length <= 320 && !used[`review:${r.id}`]

export const buildIdeas = ({ trade = 'hair', posts = [], slots = null, reviews = [], team = [], now = new Date(), max = 4, salonName = '' } = {}) => {
  const used = usedSources()
  const out = []

  if (slots && slots.times && slots.times.length) {
    const when = /^(today|tomorrow)$/i.test(slots.dayLabel) ? slots.dayLabel.toLowerCase() : `on ${slots.dayLabel}`
    const one = slots.count === 1
    out.push({
      id: `slots-${slots.date}`,
      kind: 'slots',
      title: one ? `A free slot ${when}` : `${slots.count} free slots ${when}`,
      why: 'A quick Story is the fastest way to fill them.',
      type: one ? 'oneSlot' : 'freeSlots',
      format: 'story',
      // A self-employed stylist's own times go on as theirs
      content: {
        ...(one ? { date: slots.date, time: slots.times[0] } : { date: slots.date, times: slots.times.slice(0, 3), more: slots.count > 3 }),
        ...(slots.who ? { staffId: slots.who.id, staffName: slots.who.firstName } : {}),
      },
      source: `slots:${slots.date}`,
    })
  }

  const review = (reviews || []).find(r => shareable(r, used))
  if (review) {
    out.push({
      id: `review-${review.id}`,
      kind: 'review',
      title: `${firstName(review.author) || 'A client'} left you ${review.rating || 5} stars`,
      why: 'Reviews make people trust you. Share it word for word.',
      type: 'review',
      content: { quote: review.text, name: firstName(review.author), service: review.service || '', rating: review.rating || 5 },
      source: `review:${review.id}`,
    })
  }

  const moment = upcomingMoments(trade, now)[0]
  if (moment) {
    const days = Math.max(0, Math.round((moment.date - new Date(now.getFullYear(), now.getMonth(), now.getDate())) / DAY))
    out.push({
      id: `moment-${moment.key}`,
      kind: 'moment',
      title: days === 0 ? `${moment.name} is today` : `${moment.name} in ${days} ${days === 1 ? 'day' : 'days'}`,
      why: moment.type === 'giftVouchers' ? 'People start looking for gifts now.' : 'Get it in front of people before they book elsewhere.',
      ...momentIdea(moment, salonName),
      source: `moment:${moment.key}`,
    })
  }

  const introduced = new Set(posts.filter(p => p.type === 'team').map(p => firstName(p.content && p.content.name).toLowerCase()))
  const newFace = (team || []).find(m => m && m.name && !introduced.has(firstName(m.name).toLowerCase()) && !used[`team:${m.id}`])
  if (newFace) {
    out.push({
      id: `team-${newFace.id}`,
      kind: 'team',
      title: `Introduce ${firstName(newFace.name)}`,
      why: 'People book people. A face makes it easy.',
      type: 'team',
      content: { name: firstName(newFace.name), ...(newFace.role ? { role: newFace.role } : {}) },
      photos: newFace.image ? { main: { src: newFace.image, fx: 0.5, fy: 0.35, zoom: 1 } } : undefined,
      // No photo: the name-only design, never a stock portrait
      ...(newFace.image ? {} : { design: { family: 'layouts', layout: 'monogram' } }),
      source: `team:${newFace.id}`,
    })
  }

  const lastPosted = posts.filter(p => p.status === 'posted' && p.postedAt).map(p => new Date(p.postedAt)).sort((a, b) => b - a)[0]
  if (!lastPosted || now - lastPosted > 7 * DAY) {
    out.push({
      id: 'fresh-work',
      kind: 'quiet',
      title: lastPosted ? 'It’s been a week since your last post' : 'Start with your best work',
      why: 'One great photo is enough. Posting often matters more than posting perfectly.',
      type: 'freshWork',
      content: {},
      source: null,
    })
  }

  return out.slice(0, max)
}
